import { useState, type FormEvent } from "react";
import { Badge, Button, EmptyState, Input, QueryStateBoundary } from "@/components/ui";
import {
  useAiSeoOverview,
  useConnectGsc,
  useCreateSeoKeyword,
  useGscStatus,
  useRunAiSeoCheck,
  useSeoOverview,
} from "@/api/hooks/useMarketing";
import {
  AI_PROVIDERS,
  AI_PROVIDER_LABELS,
  type AiProvider,
  type AiSeoSnapshot,
  type AiSeoTrendPoint,
  type ByProviderMap,
  type ByQueryRow,
  type ConfiguredProviders,
  type SeoKeywordRow,
} from "@/api/types/marketing";

/**
 * Marketing/SEO dashboard for Chris: two stacked sections. **AI Search
 * Visibility** is the headline — how often The Fox & The Owl surfaces
 * when someone asks Claude/ChatGPT/Perplexity/Gemini for a Bulverde venue, since that's a channel no prior CRM instruments. **Google
 * Rankings** is the traditional SEO half via Search Console.
 *
 * Every number rendered here comes straight off the API response — this
 * file only formats (percent rounding, date labels) and lays out; it never
 * derives a rate from raw counts (house rule: rates/counts are
 * server-authoritative).
 */
export function MarketingPage() {
  return (
    <div className="flex flex-col gap-10 p-6 sm:p-8">
      <div>
        <h1 className="font-display text-2xl font-bold text-text-primary">Marketing</h1>
        <p className="mt-1 text-sm text-text-secondary">
          AI search visibility and Google rankings for The Fox & The Owl.
        </p>
      </div>

      <AiSeoSection />
      <GoogleRankingsSection />
    </div>
  );
}

/**
 * A rate field on the wire may arrive as a fraction (0.42) or as a whole
 * percent (42) — the plan's contract names them `mention_rate` etc.
 * without pinning the shape. Treat <= 1 as a fraction, anything above as
 * an already-scaled percent, so either server convention renders sanely.
 */
function formatRatePct(rate: number | null | undefined): string {
  if (rate == null || Number.isNaN(rate)) return "—";
  const pct = rate <= 1 ? rate * 100 : rate;
  return `${Math.round(pct)}%`;
}

function ratePctValue(rate: number | null | undefined): number {
  if (rate == null || Number.isNaN(rate)) return 0;
  const pct = rate <= 1 ? rate * 100 : rate;
  return Math.max(0, Math.min(100, pct));
}

// ---- AI Search Visibility -------------------------------------------------------

function AiSeoSection() {
  const { data: overview, isLoading, error, refetch } = useAiSeoOverview();
  const runCheck = useRunAiSeoCheck();

  function handleRun() {
    void runCheck.mutateAsync();
  }

  return (
    <section data-testid="ai-seo-section" className="flex flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="font-display text-lg font-bold text-text-primary">AI Search Visibility</h2>
          <p className="mt-1 max-w-xl text-sm text-text-secondary">
            How often The Fox & The Owl comes up when people ask AI assistants for a Bulverde
            venue — mentioned, recommended, or cited with a link.
          </p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <Button
            onClick={handleRun}
            disabled={runCheck.isPending}
            data-testid="run-ai-seo-check-btn"
          >
            {runCheck.isPending ? "Running..." : "Run visibility check"}
          </Button>
          {runCheck.isPending && (
            <p className="text-xs text-text-muted" data-testid="run-ai-seo-check-note">
              This can take a minute — it hits live AI APIs.
            </p>
          )}
          {runCheck.isError && (
            <p className="text-xs text-danger">Check failed to run. Try again.</p>
          )}
        </div>
      </div>

      <QueryStateBoundary isLoading={isLoading} error={error} onRetry={() => void refetch()}>
        {overview && (
          <>
            {overview.note && (
              <p className="text-xs text-text-muted" data-testid="ai-seo-note">
                {overview.note}
              </p>
            )}
            {overview.latest ? (
              <div className="flex flex-col gap-6">
                <AiSeoHeroStats latest={overview.latest} />
                <ProviderBreakdownCard
                  byProvider={overview.latest.by_provider}
                  configured={overview.configured_providers}
                />
                <TrendCard trend={overview.trend} />
                <ByQueryCard rows={overview.latest.by_query} />
              </div>
            ) : (
              <EmptyState
                icon="🤖"
                title="Run your first AI-visibility check"
                description="See how often The Fox & The Owl shows up when people ask Claude, ChatGPT, Perplexity, or Gemini for a Bulverde venue."
                action={{
                  label: runCheck.isPending ? "Running..." : "Run visibility check",
                  onClick: handleRun,
                }}
              />
            )}
          </>
        )}
      </QueryStateBoundary>
    </section>
  );
}

function AiSeoHeroStats({ latest }: { latest: AiSeoSnapshot }) {
  return (
    <div
      className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4"
      data-testid="ai-seo-hero"
    >
      <StatTile
        testId="stat-mention-rate"
        label="Mention rate"
        value={formatRatePct(latest.mention_rate)}
        sub={`Week of ${latest.week}`}
      />
      <StatTile
        testId="stat-recommend-rate"
        label="Recommend rate"
        value={formatRatePct(latest.recommend_rate)}
      />
      <StatTile testId="stat-cite-rate" label="Cite rate" value={formatRatePct(latest.cite_rate)} />
      <StatTile
        testId="stat-total-checks"
        label="Total checks"
        value={String(latest.total_checks ?? 0)}
      />
    </div>
  );
}

function StatTile({
  testId,
  label,
  value,
  sub,
}: {
  testId: string;
  label: string;
  value: string;
  sub?: string;
}) {
  return (
    <div className="rounded-lg border border-border bg-bg-card p-5" data-testid={testId}>
      <p className="text-sm font-medium text-text-secondary">{label}</p>
      <p
        className="mt-1 font-display text-3xl font-bold text-text-primary"
        data-testid={`${testId}-value`}
      >
        {value}
      </p>
      {sub && <p className="mt-1 text-xs text-text-muted">{sub}</p>}
    </div>
  );
}

function ProviderBreakdownCard({
  byProvider,
  configured,
}: {
  byProvider: ByProviderMap;
  configured: ConfiguredProviders;
}) {
  return (
    <div className="rounded-lg border border-border bg-bg-card p-5" data-testid="ai-seo-provider-card">
      <h3 className="mb-3 text-sm font-semibold text-text-primary">By provider</h3>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {AI_PROVIDERS.map((provider) => (
          <ProviderTile
            key={provider}
            provider={provider}
            stats={byProvider[provider]}
            isConfigured={configured[provider] ?? false}
          />
        ))}
      </div>
    </div>
  );
}

function ProviderTile({
  provider,
  stats,
  isConfigured,
}: {
  provider: AiProvider;
  stats: ByProviderMap[string] | undefined;
  isConfigured: boolean;
}) {
  return (
    <div
      className="rounded-md border border-border p-3"
      data-testid={`provider-tile-${provider}`}
    >
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="text-sm font-medium text-text-primary">{AI_PROVIDER_LABELS[provider]}</span>
        {!isConfigured && (
          <Badge variant="secondary" size="sm" data-testid={`provider-not-configured-${provider}`}>
            Not configured
          </Badge>
        )}
      </div>
      {!isConfigured ? (
        <p className="text-xs text-text-muted">Add an API key to include this provider.</p>
      ) : stats ? (
        <dl className="grid grid-cols-3 gap-x-2 gap-y-1 text-xs">
          <div>
            <dt className="text-text-muted">Checks</dt>
            <dd className="font-medium text-text-primary">{stats.checks}</dd>
          </div>
          <div>
            <dt className="text-text-muted">Mentioned</dt>
            <dd className="font-medium text-text-primary">{stats.mentioned}</dd>
          </div>
          <div>
            <dt className="text-text-muted">Cited</dt>
            <dd className="font-medium text-text-primary">{stats.cited}</dd>
          </div>
        </dl>
      ) : (
        <p className="text-xs text-text-muted">No checks yet.</p>
      )}
    </div>
  );
}

function TrendCard({ trend }: { trend: AiSeoTrendPoint[] }) {
  return (
    <div className="rounded-lg border border-border bg-bg-card p-5" data-testid="ai-seo-trend-card">
      <h3 className="mb-3 text-sm font-semibold text-text-primary">Mention rate trend</h3>
      {trend.length === 0 ? (
        <p className="text-sm text-text-muted">
          Not enough history yet — run a few weekly checks to see a trend.
        </p>
      ) : (
        <div className="flex items-end gap-3" data-testid="ai-seo-trend-bars">
          {trend.map((point, i) => {
            const pct = ratePctValue(point.mention_rate);
            return (
              <div
                key={`${point.week}-${i}`}
                className="flex flex-1 flex-col items-center gap-1"
                data-testid={`trend-bar-${point.week}`}
              >
                <div className="flex h-24 w-full items-end justify-center rounded bg-bg-muted">
                  <div className="w-2/3 rounded-t bg-copper" style={{ height: `${pct}%` }} />
                </div>
                <span className="text-xs font-medium text-text-primary">
                  {formatRatePct(point.mention_rate)}
                </span>
                <span className="text-[11px] text-text-muted">{point.week}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ByQueryCard({ rows }: { rows: ByQueryRow[] }) {
  return (
    <div className="rounded-lg border border-border bg-bg-card p-5" data-testid="ai-seo-by-query-card">
      <h3 className="mb-3 text-sm font-semibold text-text-primary">By query</h3>
      {rows.length === 0 ? (
        <p className="text-sm text-text-muted">No per-query results yet.</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border" data-testid="ai-seo-by-query-table">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-border bg-bg-hover/50 text-xs uppercase tracking-wide text-text-muted">
              <tr>
                <th className="px-4 py-2 font-medium">Query</th>
                <th className="px-4 py-2 font-medium">Mentioned</th>
                <th className="px-4 py-2 font-medium">Recommended</th>
                <th className="px-4 py-2 font-medium">Cited</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rows.map((row, i) => (
                <tr key={`${row.query}-${i}`} data-testid="ai-seo-by-query-row">
                  <td className="px-4 py-3 text-text-primary">{row.query}</td>
                  <td className="px-4 py-3 text-text-secondary">{row.mentioned ? "✅" : "—"}</td>
                  <td className="px-4 py-3 text-text-secondary">{row.recommended ? "✅" : "—"}</td>
                  <td className="px-4 py-3 text-text-secondary">{row.cited ? "✅" : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---- Google Rankings ------------------------------------------------------------

function GoogleRankingsSection() {
  const { data: overview, isLoading, error, refetch } = useSeoOverview();
  const { data: gscStatus } = useGscStatus();
  const connectGsc = useConnectGsc();
  const createKeyword = useCreateSeoKeyword();

  const [phrase, setPhrase] = useState("");
  const [intent, setIntent] = useState("");

  // seo/overview.connected is the primary signal; gsc/status is a fallback
  // in case overview hasn't loaded yet or omits the field.
  const connected = overview?.connected ?? gscStatus?.connected ?? false;

  async function handleConnect() {
    const authUrl = await connectGsc.mutateAsync();
    if (authUrl) {
      window.open(authUrl, "_blank", "noopener,noreferrer");
    }
  }

  function handleAddKeyword(event: FormEvent) {
    event.preventDefault();
    const trimmed = phrase.trim();
    if (!trimmed) return;
    createKeyword.mutate(
      { phrase: trimmed, intent: intent.trim() || undefined },
      {
        onSuccess: () => {
          setPhrase("");
          setIntent("");
        },
      },
    );
  }

  return (
    <section data-testid="google-rankings-section" className="flex flex-col gap-6">
      <div>
        <h2 className="font-display text-lg font-bold text-text-primary">Google Rankings</h2>
        <p className="mt-1 max-w-xl text-sm text-text-secondary">
          Keyword positions, clicks, impressions, and CTR from Google Search Console.
        </p>
      </div>

      <QueryStateBoundary isLoading={isLoading} error={error} onRetry={() => void refetch()}>
        {!connected ? (
          <div className="rounded-lg border border-border bg-bg-card p-6" data-testid="gsc-connect-card">
            <h3 className="mb-2 text-sm font-semibold text-text-primary">
              Connect Google Search Console
            </h3>
            <p className="mb-4 text-sm text-text-secondary">
              Connecting lets FoxCRM pull real keyword positions and click data for
              thefoxandtheowl.net straight from Google — no manual CSV exports.
            </p>
            {overview?.note && (
              <p className="mb-4 text-xs text-text-muted" data-testid="gsc-note">
                {overview.note}
              </p>
            )}
            <Button
              onClick={() => void handleConnect()}
              disabled={connectGsc.isPending}
              data-testid="connect-gsc-btn"
            >
              {connectGsc.isPending ? "Connecting..." : "Connect Google Search Console"}
            </Button>
          </div>
        ) : (
          <div className="rounded-lg border border-border bg-bg-card p-5" data-testid="keywords-card">
            <h3 className="mb-3 text-sm font-semibold text-text-primary">Tracked keywords</h3>

            {overview && overview.keywords.length > 0 ? (
              <KeywordsTable rows={overview.keywords} />
            ) : (
              <EmptyState
                icon="🔎"
                title="No keywords tracked yet"
                description="Add a keyword below to start tracking its Google position."
              />
            )}

            <form
              onSubmit={handleAddKeyword}
              className="mt-5 flex flex-wrap items-end gap-3 border-t border-border pt-4"
              data-testid="add-keyword-form"
            >
              <div className="min-w-[14rem] flex-1">
                <label
                  className="mb-1 block text-xs font-medium text-text-secondary"
                  htmlFor="keyword-phrase"
                >
                  Keyword
                </label>
                <Input
                  id="keyword-phrase"
                  value={phrase}
                  onChange={(e) => setPhrase(e.target.value)}
                  placeholder="bulverde event venue"
                />
              </div>
              <div className="min-w-[10rem]">
                <label
                  className="mb-1 block text-xs font-medium text-text-secondary"
                  htmlFor="keyword-intent"
                >
                  Intent (optional)
                </label>
                <Input
                  id="keyword-intent"
                  value={intent}
                  onChange={(e) => setIntent(e.target.value)}
                  placeholder="transactional"
                />
              </div>
              <Button
                type="submit"
                disabled={createKeyword.isPending || !phrase.trim()}
                data-testid="add-keyword-btn"
              >
                {createKeyword.isPending ? "Adding..." : "Add keyword"}
              </Button>
            </form>
          </div>
        )}
      </QueryStateBoundary>
    </section>
  );
}

function formatMetric(value: number | null | undefined): string {
  return value == null ? "—" : String(value);
}

function formatCtr(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return "—";
  const pct = value <= 1 ? value * 100 : value;
  return `${pct.toFixed(1)}%`;
}

function KeywordsTable({ rows }: { rows: SeoKeywordRow[] }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-border" data-testid="keywords-table">
      <table className="w-full text-left text-sm">
        <thead className="border-b border-border bg-bg-hover/50 text-xs uppercase tracking-wide text-text-muted">
          <tr>
            <th className="px-4 py-2 font-medium">Phrase</th>
            <th className="px-4 py-2 font-medium">Position</th>
            <th className="px-4 py-2 font-medium">Clicks</th>
            <th className="px-4 py-2 font-medium">Impressions</th>
            <th className="px-4 py-2 font-medium">CTR</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {rows.map((row, i) => (
            <tr key={`${row.phrase}-${i}`} data-testid="keyword-row">
              <td className="px-4 py-3 font-medium text-text-primary">{row.phrase}</td>
              <td className="px-4 py-3 text-text-secondary">{formatMetric(row.position)}</td>
              <td className="px-4 py-3 text-text-secondary">{formatMetric(row.clicks)}</td>
              <td className="px-4 py-3 text-text-secondary">{formatMetric(row.impressions)}</td>
              <td className="px-4 py-3 text-text-secondary">{formatCtr(row.ctr)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
